import React, { useState, useEffect } from 'react';
import { 
  Home, Sun, Shield, Zap, Battery, ArrowRight, Check, 
  Calculator, Phone, ChevronDown, Lightbulb, Leaf, TrendingUp,
  Clock, Award, Users, Star
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import FloatingButtons from '../components/FloatingButtons';

const ServicioResidencialPage = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const benefits = [
    {
      icon: TrendingUp,
      title: 'Ahorro desde el Primer Mes',
      description: 'Reducí desde el 85% al 100% de tu factura de ANDE. El ahorro comienza inmediatamente después de la instalación.'
    },
    {
      icon: Home,
      title: 'Aumentá el Valor de tu Propiedad',
      description: 'Las viviendas con paneles solares tienen mayor valor de reventa y son más atractivas para compradores conscientes.'
    },
    {
      icon: Leaf,
      title: 'Energía 100% Limpia',
      description: 'Contribuí al medio ambiente con energía renovable. Cada kWh solar evita emisiones de CO2 a la atmósfera.'
    },
    {
      icon: Shield,
      title: '25 Años de Garantía',
      description: 'Respaldamos nuestros paneles con garantía extendida. Tranquilidad total para vos y tu familia.'
    },
    {
      icon: Zap,
      title: 'Independencia Energética',
      description: 'Producí tu propia electricidad y dejá de depender completamente de la red eléctrica tradicional.'
    },
    {
      icon: Battery,
      title: 'Opción de Baterías',
      description: 'Agregá almacenamiento para tener energía incluso durante cortes de luz o en horarios nocturnos.'
    }
  ];

  const processSteps = [
    {
      number: '01',
      title: 'Consulta Gratuita',
      description: 'Analizamos tu consumo eléctrico actual y las características de tu vivienda sin costo alguno.'
    },
    {
      number: '02',
      title: 'Diseño Personalizado',
      description: 'Creamos un sistema solar a medida según tu consumo, espacio disponible y objetivos de ahorro.'
    },
    {
      number: '03',
      title: 'Presupuesto Detallado',
      description: 'Te presentamos costos, tiempos de retorno de inversión y opciones de financiamiento disponibles.'
    },
    {
      number: '04',
      title: 'Instalación Profesional',
      description: 'Nuestro equipo certificado instala tu sistema en 3-5 días hábiles con mínima interrupción.'
    },
    {
      number: '05',
      title: 'Activación y Monitoreo',
      description: 'Configuramos tu sistema, te capacitamos en su uso y activamos el monitoreo remoto.'
    }
  ];

  const packages = [
    {
      name: 'Hogar Básico',
      power: '3 kW',
      panels: '6-8 paneles',
      idealFor: 'Casas pequeñas o departamentos',
      monthlyBill: 'Gs. 150.000 - 300.000',
      features: ['Inversor de alta eficiencia', 'Estructura de montaje', 'Monitoreo básico', 'Garantía 25 años'],
      highlighted: false
    },
    {
      name: 'Hogar Confort',
      power: '5 kW',
      panels: '10-12 paneles',
      idealFor: 'Casas medianas con aire acondicionado',
      monthlyBill: 'Gs. 300.000 - 600.000',
      features: ['Inversor premium', 'Estructura reforzada', 'App de monitoreo', 'Garantía 25 años', 'Mantenimiento 1 año'],
      highlighted: true
    },
    {
      name: 'Hogar Premium',
      power: '8+ kW',
      panels: '16+ paneles',
      idealFor: 'Casas grandes con alto consumo',
      monthlyBill: 'Gs. 600.000+',
      features: ['Inversor híbrido', 'Compatible con baterías', 'Monitoreo avanzado', 'Garantía 25 años', 'Mantenimiento 2 años', 'Soporte prioritario'],
      highlighted: false
    }
  ];

  const faqs = [
    {
      question: '¿Cuánto puedo ahorrar realmente en mi factura?',
      answer: 'Dependiendo del tamaño de tu sistema y tu consumo, podés reducir desde el 85% hasta el 100% de tu factura de ANDE. El ahorro es visible desde el primer mes de operación. Un sistema bien dimensionado puede eliminar casi por completo tu dependencia de la red eléctrica.'
    },
    {
      question: '¿Qué pasa cuando no hay sol o llueve?',
      answer: 'Tu sistema sigue conectado a la red de ANDE. En días nublados, los paneles siguen generando (aunque menos), y si necesitás más energía, la tomás de la red normalmente. También ofrecemos sistemas con baterías para mayor independencia.'
    },
    {
      question: '¿Cuánto dura la instalación?',
      answer: 'Una instalación residencial típica se completa en 3 a 5 días hábiles. Esto incluye montaje de paneles, instalación del inversor, cableado y configuración del sistema de monitoreo.'
    },
    {
      question: '¿Necesito hacer cambios en mi instalación eléctrica?',
      answer: 'En la mayoría de los casos, no se requieren modificaciones significativas. Nuestro equipo técnico evalúa tu instalación actual y realiza las adecuaciones necesarias como parte del servicio.'
    },
    {
      question: '¿Qué mantenimiento requiere el sistema?',
      answer: 'Los sistemas solares requieren muy poco mantenimiento. Recomendamos una limpieza de paneles cada 6 meses (o cuando haya mucho polvo) y una revisión técnica anual. Ofrecemos planes de mantenimiento incluidos.'
    },
    {
      question: '¿Tienen opciones de financiamiento?',
      answer: 'Sí, trabajamos con varios bancos y financieras para ofrecerte planes de pago accesibles. También contamos con financiamiento propio. El ahorro mensual en tu factura puede cubrir gran parte de la cuota.'
    }
  ];

  const testimonials = [
    {
      name: 'Carlos Mendoza',
      location: 'Asunción',
      text: 'Mi factura bajó de Gs. 450.000 a menos de Gs. 50.000. La instalación fue rápida y profesional.',
      rating: 5
    },
    {
      name: 'María González',
      location: 'Luque',
      text: 'Excelente inversión. Ya recuperé parte del costo y tengo energía limpia para mi familia.',
      rating: 5
    },
    {
      name: 'Roberto Fernández',
      location: 'San Lorenzo',
      text: 'El equipo de Zenex fue muy profesional. Respondieron todas mis dudas y el sistema funciona perfecto.',
      rating: 5
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navigation isScrolled={isScrolled} />

      {/* Hero Section */}
      <section className="relative pt-24 pb-20 md:pt-32 md:pb-28 overflow-hidden" data-testid="residential-hero">
        <div className="absolute inset-0 bg-gradient-to-br from-zinc-900 via-zinc-800 to-zinc-900"></div>
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-20 left-10 w-72 h-72 bg-[#90EE90] rounded-full filter blur-[100px]"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-[#90EE90] rounded-full filter blur-[120px]"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-8 sm:gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#90EE90]/20 rounded-full mb-4 sm:mb-6">
                <Home className="w-4 h-4 text-[#90EE90]" />
                <span className="text-sm font-medium text-[#90EE90]">Instalacion Residencial</span>
              </div>
              
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 sm:mb-6 leading-tight">
                Transforma tu Hogar en una
                <span className="text-[#90EE90]"> Central de Energia Limpia</span>
              </h1>
              
              <p className="text-base sm:text-lg md:text-xl text-zinc-300 mb-6 sm:mb-8 leading-relaxed">
                Reducí desde el 85% al 100% de tu factura eléctrica con paneles solares de última generación. 
                Ahorro visible desde el primer mes y garantía de 25 años.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={() => navigate('/calculadora')}
                  className="flex items-center justify-center gap-2 px-8 py-4 bg-[#90EE90] hover:bg-[#7DD87D] text-zinc-900 font-bold rounded-xl transition-all duration-300 hover:scale-105"
                  data-testid="cta-calculator"
                >
                  <Calculator size={20} />
                  Calculá tu Ahorro
                </button>
                <a
                  href="https://wa.me/595991344956?text=Hola%20Zenex!%20Me%20interesa%20la%20instalación%20solar%20residencial"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 px-8 py-4 bg-white/10 hover:bg-white/20 text-white font-bold rounded-xl transition-all duration-300 border border-white/20"
                  data-testid="cta-whatsapp"
                >
                  <Phone size={20} />
                  Contactar Ahora
                </a>
              </div>
            </div>

            {/* Hero Image */}
            <div className="relative hidden lg:block">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="https://customer-assets.emergentagent.com/job_3a22e1e9-596e-48c2-b9b4-3588e12d7d49/artifacts/omt01bnv_db891bb0-2cc5-11ef-be76-d63d9ee0b1e9.jpeg"
                  alt="Casa con paneles solares"
                  className="w-full h-[500px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
              </div>
              
              {/* Floating Card */}
              <div className="absolute -bottom-6 -left-6 bg-white p-6 rounded-xl shadow-xl">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-[#90EE90]/20 rounded-full">
                    <TrendingUp className="w-6 h-6 text-[#14532D]" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-zinc-900">-85%</div>
                    <div className="text-sm text-zinc-500">en tu factura</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 sm:py-20 md:py-28 bg-zinc-50" data-testid="benefits-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="text-sm font-medium uppercase tracking-widest text-[#90EE90] mb-4 block">
              ¿Por qué elegir energía solar?
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-zinc-900 mb-4">
              Beneficios Exclusivos para tu Hogar
            </h2>
            <p className="text-lg text-zinc-600 max-w-2xl mx-auto">
              Descubrí todas las ventajas de generar tu propia energía limpia
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <div 
                  key={index}
                  className="bg-white p-8 rounded-2xl border border-zinc-200 hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
                  data-testid={`benefit-card-${index}`}
                >
                  <div className="p-3 bg-[#90EE90]/20 rounded-xl w-fit mb-6">
                    <Icon className="w-6 h-6 text-[#14532D]" />
                  </div>
                  <h3 className="text-xl font-bold text-zinc-900 mb-3">{benefit.title}</h3>
                  <p className="text-zinc-600 leading-relaxed">{benefit.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-16 sm:py-20 md:py-28" data-testid="process-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="text-sm font-medium uppercase tracking-widest text-[#90EE90] mb-4 block">
              Proceso Simple
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-zinc-900 mb-4">
              ¿Cómo Funciona?
            </h2>
            <p className="text-lg text-zinc-600 max-w-2xl mx-auto">
              De la consulta inicial a la energía solar en tu techo en 5 simples pasos
            </p>
          </div>

          <div className="relative">
            {/* Connection Line */}
            <div className="hidden lg:block absolute top-1/2 left-0 right-0 h-0.5 bg-[#90EE90]/30 -translate-y-1/2"></div>
            
            <div className="grid md:grid-cols-5 gap-8">
              {processSteps.map((step, index) => (
                <div key={index} className="relative text-center">
                  <div className="relative z-10 mx-auto w-16 h-16 bg-[#90EE90] rounded-full flex items-center justify-center mb-6 shadow-lg">
                    <span className="text-xl font-bold text-zinc-900">{step.number}</span>
                  </div>
                  <h3 className="text-lg font-bold text-zinc-900 mb-2">{step.title}</h3>
                  <p className="text-sm text-zinc-600">{step.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Packages Section */}
      <section className="py-16 sm:py-20 md:py-28 bg-zinc-900" data-testid="packages-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="text-sm font-medium uppercase tracking-widest text-[#90EE90] mb-4 block">
              Soluciones a tu Medida
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Paquetes Residenciales
            </h2>
            <p className="text-lg text-zinc-400 max-w-2xl mx-auto">
              Elegí el sistema ideal según tu consumo y necesidades
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {packages.map((pkg, index) => (
              <div 
                key={index}
                className={`relative rounded-2xl overflow-hidden transition-all duration-300 hover:scale-105 ${
                  pkg.highlighted 
                    ? 'bg-gradient-to-b from-[#90EE90]/20 to-zinc-800 border-2 border-[#90EE90]' 
                    : 'bg-zinc-800 border border-zinc-700'
                }`}
                data-testid={`package-card-${index}`}
              >
                {pkg.highlighted && (
                  <div className="absolute top-0 left-0 right-0 bg-[#90EE90] text-zinc-900 text-center py-2 text-sm font-bold">
                    MÁS POPULAR
                  </div>
                )}
                
                <div className={`p-8 ${pkg.highlighted ? 'pt-14' : ''}`}>
                  <h3 className="text-2xl font-bold text-white mb-2">{pkg.name}</h3>
                  <div className="text-4xl font-bold text-[#90EE90] mb-4">{pkg.power}</div>
                  <p className="text-zinc-400 mb-2">{pkg.panels}</p>
                  <p className="text-sm text-zinc-500 mb-6">Ideal para: {pkg.idealFor}</p>
                  
                  <div className="bg-zinc-700/50 rounded-lg p-4 mb-6">
                    <p className="text-sm text-zinc-400">Factura mensual típica</p>
                    <p className="text-lg font-bold text-white">{pkg.monthlyBill}</p>
                  </div>

                  <ul className="space-y-3 mb-8">
                    {pkg.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center gap-3 text-zinc-300">
                        <Check className="w-5 h-5 text-[#90EE90] flex-shrink-0" />
                        <span className="text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <button
                    onClick={() => navigate('/calculadora')}
                    className={`w-full py-4 rounded-xl font-bold transition-all duration-300 ${
                      pkg.highlighted
                        ? 'bg-[#90EE90] text-zinc-900 hover:bg-[#7DD87D]'
                        : 'bg-zinc-700 text-white hover:bg-zinc-600'
                    }`}
                  >
                    Solicitar Cotización
                  </button>
                </div>
              </div>
            ))}
          </div>

          <p className="text-center text-zinc-500 mt-8 text-sm">
            * Los precios y especificaciones pueden variar según tu ubicación y requerimientos específicos.
          </p>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-16 sm:py-20 md:py-28 bg-zinc-50" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="text-sm font-medium uppercase tracking-widest text-[#90EE90] mb-4 block">
              Testimonios
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-zinc-900 mb-4">
              Lo que Dicen Nuestros Clientes
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div 
                key={index}
                className="bg-white p-8 rounded-2xl border border-zinc-200 shadow-sm"
                data-testid={`testimonial-card-${index}`}
              >
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 fill-[#90EE90] text-[#90EE90]" />
                  ))}
                </div>
                <p className="text-zinc-600 mb-6 italic">"{testimonial.text}"</p>
                <div>
                  <p className="font-bold text-zinc-900">{testimonial.name}</p>
                  <p className="text-sm text-zinc-500">{testimonial.location}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 sm:py-20 md:py-28" data-testid="faq-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="text-sm font-medium uppercase tracking-widest text-[#90EE90] mb-4 block">
              Preguntas Frecuentes
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-zinc-900 mb-4">
              ¿Tenés Dudas?
            </h2>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div 
                key={index}
                className="border border-zinc-200 rounded-xl overflow-hidden"
                data-testid={`faq-item-${index}`}
              >
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full flex items-center justify-between p-6 text-left bg-white hover:bg-zinc-50 transition-colors"
                >
                  <span className="font-semibold text-zinc-900 pr-4">{faq.question}</span>
                  <ChevronDown 
                    className={`w-5 h-5 text-zinc-500 transition-transform ${openFaq === index ? 'rotate-180' : ''}`}
                  />
                </button>
                {openFaq === index && (
                  <div className="px-6 pb-6 bg-white">
                    <p className="text-zinc-600 leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-16 sm:py-20 md:py-28 bg-gradient-to-r from-[#14532D] to-[#166534]" data-testid="final-cta">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 md:px-12 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/20 rounded-full mb-6">
            <Lightbulb className="w-4 h-4 text-[#90EE90]" />
            <span className="text-sm font-medium text-white">¿Listo para empezar?</span>
          </div>
          
          <h2 className="text-3xl md:text-5xl font-bold text-white mb-6">
            Empezá a Ahorrar Hoy Mismo
          </h2>
          <p className="text-xl text-white/80 mb-10 max-w-2xl mx-auto">
            Obtené una cotización personalizada gratuita y descubrí cuánto podés ahorrar con energía solar.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => navigate('/calculadora')}
              className="flex items-center justify-center gap-2 px-8 py-4 bg-[#90EE90] hover:bg-[#7DD87D] text-zinc-900 font-bold rounded-xl transition-all duration-300 hover:scale-105"
            >
              <Calculator size={20} />
              Usar Calculadora Solar
            </button>
            <a
              href="https://wa.me/595991344956?text=Hola%20Zenex!%20Quiero%20más%20información%20sobre%20instalación%20residencial"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 px-8 py-4 bg-white text-zinc-900 font-bold rounded-xl transition-all duration-300 hover:bg-zinc-100"
            >
              <Phone size={20} />
              Hablar con un Asesor
            </a>
          </div>
        </div>
      </section>

      <Footer />
      <FloatingButtons />
    </div>
  );
};

export default ServicioResidencialPage;
